import { t as tr } from "./i18n";
/**
 * Voice capture in the browser. Records with MediaRecorder, shows a live level,
 * and prepares an upload the transcriber can read: audio/mp4 as recorded when the
 * browser can produce it, otherwise the webm/ogg opus recording is transcoded
 * client-side to 16 kHz mono 16-bit WAV.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface RecorderResult {
  blob: Blob;
  mime: string;
  durationMs: number;
  recordedAt: string;
  /** Loudest sample over the recording, dBFS (≤ 0). Undefined when the meter couldn't run. */
  peakDb?: number;
  /** Mean RMS over the recording, dBFS (≤ 0). */
  avgDb?: number;
}

export type RecorderState = "idle" | "requesting" | "starting" | "recording" | "finishing";
export type MicrophoneAccess = PermissionState | "checking" | "unknown" | "unavailable" | "ready";

export const METER_BARS = 28;
const MIN_MS = 500;
const MIC_CONSTRAINTS: MediaStreamConstraints = { audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } };
const MIC_SETUP_KEY = "hondo.v2.microphone-setup";

// This remembers completed setup, not a browser permission grant. Only a user
// gesture opens the microphone, and getUserMedia still enforces current access.
function microphoneSetupRemembered(fallback = false): boolean {
  try { return localStorage.getItem(MIC_SETUP_KEY) === "true"; }
  catch { return fallback; }
}

async function microphonePermission(): Promise<PermissionStatus | null> {
  try {
    return await navigator.permissions.query({ name: "microphone" as PermissionName });
  } catch {
    // Some browsers cannot query microphone access. Never probe by opening it.
    return null;
  }
}

export function isRecordingSupported(): boolean {
  return typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";
}

export function pickRecorderMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/mp4", "audio/mp4;codecs=mp4a.40.2", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* older browsers */
    }
  }
  return "";
}

export function baseMime(mime: string): string {
  return (mime.split(";")[0] ?? "").trim().toLowerCase();
}

function micError(e: unknown, denied: boolean): string {
  const name = e instanceof DOMException ? e.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return denied
    ? tr("Microphone access is blocked. Allow it for this site in your browser's settings, then try again. You can also type a note.")
    : tr("Microphone access wasn't allowed. Try again and choose Allow, or check this site's microphone setting in your browser. You can also type a note.");
  if (name === "NotFoundError" || name === "OverconstrainedError") return tr("No microphone found. Plug one in, or type the note instead.");
  if (name === "NotReadableError") return tr("The microphone is busy in another app.");
  return e instanceof Error && e.message ? tr("Couldn't start recording: {{value1}}", { value1: e.message }) : tr("Couldn't start recording.");
}

interface Internals {
  stream: MediaStream | null;
  rec: MediaRecorder | null;
  ctx: AudioContext | null;
  analyser: AnalyserNode | null;
  raf: number;
  timer: number;
  chunks: Blob[];
  startedAt: number;
  recordedAt: string;
  frame: number;
  levels: number[];
  cancelled: boolean;
  peak: number;
  rmsSum: number;
  rmsFrames: number;
}

export function useRecorder(onResult: (r: RecorderResult) => void) {
  const [state, setState] = useState<RecorderState>("idle");
  const [access, setAccess] = useState<MicrophoneAccess>("checking");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [levels, setLevels] = useState<number[]>(() => new Array<number>(METER_BARS).fill(0));
  const [error, setError] = useState<string | null>(null);
  const stateRef = useRef<RecorderState>("idle");
  const accessRef = useRef<MicrophoneAccess>("checking");
  const permissionRef = useRef<PermissionStatus | null>(null);
  const setupRef = useRef(microphoneSetupRemembered());
  const generation = useRef(0);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const it = useRef<Internals>({
    stream: null,
    rec: null,
    ctx: null,
    analyser: null,
    raf: 0,
    timer: 0,
    chunks: [],
    startedAt: 0,
    recordedAt: "",
    frame: 0,
    levels: [],
    cancelled: false,
    peak: 0,
    rmsSum: 0,
    rmsFrames: 0,
  });

  const setSt = (s: RecorderState) => {
    stateRef.current = s;
    setState(s);
  };

  const setMicAccess = useCallback((next: MicrophoneAccess) => {
    accessRef.current = next;
    setAccess(next);
  }, []);

  const rememberSetup = useCallback((completed: boolean) => {
    setupRef.current = completed;
    try {
      if (completed) localStorage.setItem(MIC_SETUP_KEY, "true");
      else localStorage.removeItem(MIC_SETUP_KEY);
    } catch { /* Keep the current session usable when browser storage is blocked. */ }
  }, []);

  useEffect(() => {
    if (!isRecordingSupported()) {
      setMicAccess("unavailable");
      return;
    }
    let active = true;
    let status: PermissionStatus | null = null;
    let refreshId = 0;
    const changed = () => {
      if (status?.state === "denied") {
        rememberSetup(false);
        if (stateRef.current === "requesting" || stateRef.current === "starting") {
          generation.current++;
          setSt("idle");
        }
      }
      else if (status?.state === "granted") rememberSetup(true);
      // Permission prompts and device events may settle after a successful open.
      if (stateRef.current !== "idle" && status?.state !== "denied") return;
      // A granted permission does not prove that an unplugged or busy mic works again.
      const previous = accessRef.current;
      if (previous === "unavailable" && status?.state !== "denied") return;
      const next = status?.state === "granted" || status?.state === "denied" ? status.state
        : setupRef.current ? "ready" : status?.state ?? "unknown";
      setMicAccess(next);
      if (status && previous !== next && previous !== "unavailable") setError(null);
    };
    const refresh = async () => {
      const id = ++refreshId;
      const next = await microphonePermission();
      if (!active || id !== refreshId) return;
      status?.removeEventListener("change", changed);
      status = next;
      permissionRef.current = next;
      setupRef.current = microphoneSetupRemembered(setupRef.current);
      changed();
      status?.addEventListener("change", changed);
    };
    const visible = () => { if (document.visibilityState === "visible") void refresh(); };
    const stored = (event: StorageEvent) => { if (event.key === MIC_SETUP_KEY || event.key === null) void refresh(); };
    void refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("storage", stored);
    document.addEventListener("visibilitychange", visible);
    navigator.mediaDevices.addEventListener("devicechange", refresh);
    return () => {
      active = false;
      status?.removeEventListener("change", changed);
      permissionRef.current = null;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("storage", stored);
      document.removeEventListener("visibilitychange", visible);
      navigator.mediaDevices.removeEventListener("devicechange", refresh);
    };
  }, [rememberSetup, setMicAccess]);

  const failedToAccessMic = useCallback((e: unknown) => {
    const name = e instanceof DOMException ? e.name : "";
    const denied = permissionRef.current?.state === "denied";
    if (name === "NotAllowedError" || name === "SecurityError") {
      rememberSetup(false);
      setMicAccess(denied ? "denied" : "prompt");
    }
    else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "NotReadableError") setMicAccess("unavailable");
    setError(micError(e, denied));
  }, [rememberSetup, setMicAccess]);

  const requestAccess = useCallback(async () => {
    if (stateRef.current !== "idle" || !isRecordingSupported()) return;
    const attempt = ++generation.current;
    setError(null);
    setSt("requesting");
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      if (generation.current === attempt) {
        rememberSetup(true);
        setMicAccess("granted");
      }
    } catch (e) {
      if (generation.current === attempt) failedToAccessMic(e);
    } finally {
      // Permission setup never creates a recording or leaves the microphone running.
      stream?.getTracks().forEach((track) => track.stop());
      if (generation.current === attempt) setSt("idle");
    }
  }, [failedToAccessMic, rememberSetup, setMicAccess]);

  const teardown = useCallback(() => {
    const i = it.current;
    if (i.rec) {
      i.rec.ondataavailable = null;
      i.rec.onstop = null;
      i.rec.onerror = null;
      if (i.rec.state !== "inactive") {
        try { i.rec.stop(); } catch { /* already stopped */ }
      }
    }
    if (i.raf) cancelAnimationFrame(i.raf);
    if (i.timer) window.clearInterval(i.timer);
    i.raf = 0;
    i.timer = 0;
    i.stream?.getTracks().forEach((t) => t.stop());
    i.stream = null;
    i.analyser = null;
    if (i.ctx) {
      void i.ctx.close().catch(() => undefined);
      i.ctx = null;
    }
    i.rec = null;
    setLevels(new Array<number>(METER_BARS).fill(0));
  }, []);

  const start = useCallback(async () => {
    if (stateRef.current !== "idle" || !["granted", "ready"].includes(accessRef.current)) return;
    setError(null);
    if (!isRecordingSupported()) {
      setError(tr("This browser can't record audio. Type the note instead."));
      return;
    }
    setSt("starting");
    const attempt = ++generation.current;
    const i = it.current;
    try {
      // Open directly from this gesture. The browser checks current permission,
      // including a new prompt when its previous grant was only temporary.
      const stream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      if (generation.current !== attempt) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      i.stream = stream;
      rememberSetup(true);
      setMicAccess("granted");
      const mime = pickRecorderMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 64_000 } : undefined);
      i.rec = rec;
      i.chunks = [];
      i.cancelled = false;
      i.levels = new Array<number>(METER_BARS).fill(0);
      i.frame = 0;
      i.peak = 0;
      i.rmsSum = 0;
      i.rmsFrames = 0;
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) i.chunks.push(ev.data);
      };
      rec.onerror = () => {
        setError(tr("Recording stopped unexpectedly. Try again."));
        teardown();
        setSt("idle");
      };
      rec.onstop = () => {
        const durationMs = Date.now() - i.startedAt;
        const actualMime = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(i.chunks, { type: actualMime });
        const recordedAt = i.recordedAt;
        const cancelled = i.cancelled;
        const levelsKnown = i.rmsFrames > 0;
        const peakDb = levelsKnown ? toDbfs(i.peak) : undefined;
        const avgDb = levelsKnown ? toDbfs(i.rmsSum / i.rmsFrames) : undefined;
        teardown();
        setSt("idle");
        setElapsedMs(0);
        if (cancelled) return;
        if (durationMs < MIN_MS || blob.size === 0) {
          setError(tr("Too short. Hold the button while you talk."));
          return;
        }
        onResultRef.current({ blob, mime: actualMime, durationMs, recordedAt, peakDb, avgDb });
      };
      // Level meter.
      try {
        const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          src.connect(analyser);
          i.ctx = ctx;
          i.analyser = analyser;
          const buf = new Float32Array(analyser.fftSize);
          const loop = () => {
            const a = i.analyser;
            if (!a) return;
            a.getFloatTimeDomainData(buf);
            let sum = 0;
            let peak = 0;
            for (let k = 0; k < buf.length; k++) {
              const v = buf[k] ?? 0;
              sum += v * v;
              const abs = v < 0 ? -v : v;
              if (abs > peak) peak = abs;
            }
            const rms = Math.sqrt(sum / buf.length);
            if (peak > i.peak) i.peak = peak;
            i.rmsSum += rms;
            i.rmsFrames++;
            const level = Math.min(1, rms * 3.2);
            i.frame++;
            if (i.frame % 3 === 0) {
              i.levels = [...i.levels.slice(1), level];
              setLevels(i.levels);
            }
            i.raf = requestAnimationFrame(loop);
          };
          i.raf = requestAnimationFrame(loop);
        }
      } catch {
        /* meter is decorative */
      }
      i.startedAt = Date.now();
      i.recordedAt = new Date().toISOString();
      rec.start(250);
      i.timer = window.setInterval(() => setElapsedMs(Date.now() - i.startedAt), 100);
      setSt("recording");
    } catch (e) {
      if (generation.current !== attempt) return;
      failedToAccessMic(e);
      teardown();
      setSt("idle");
    }
  }, [failedToAccessMic, rememberSetup, setMicAccess, teardown]);

  const stop = useCallback(() => {
    const i = it.current;
    if (stateRef.current !== "recording" || !i.rec) return;
    setSt("finishing");
    try {
      i.rec.stop();
    } catch {
      teardown();
      setSt("idle");
    }
  }, [teardown]);

  const cancel = useCallback((message?: string) => {
    if (stateRef.current === "requesting" || stateRef.current === "starting") {
      generation.current++;
      teardown();
      setSt("idle");
      if (message) setError(message);
      return;
    }
    const i = it.current;
    if (!i.rec) return;
    i.cancelled = true;
    try {
      i.rec.stop();
    } catch {
      teardown();
      setSt("idle");
    }
  }, [teardown]);

  useEffect(() => () => {
    generation.current++;
    teardown();
  }, [teardown]);

  return { state, access, elapsedMs, levels, error, requestAccess, start, stop, cancel, clearError: () => setError(null), supported: isRecordingSupported() };
}

/** Linear amplitude (0..1) to dBFS, floored at -100 so silence stays a finite number. */
export function toDbfs(amplitude: number): number {
  if (!(amplitude > 0)) return -100;
  return Math.max(-100, Math.min(0, Math.round(20 * Math.log10(amplitude) * 10) / 10));
}

// --- Upload preparation ---------------------------------------------------------

function writeStr(v: DataView, off: number, s: string) {
  for (let k = 0; k < s.length; k++) v.setUint8(off + k, s.charCodeAt(k));
}

export function pcmToWav(samples: Float32Array, rate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buffer);
  writeStr(v, 0, "RIFF");
  v.setUint32(4, 36 + samples.length * 2, true);
  writeStr(v, 8, "WAVE");
  writeStr(v, 12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  writeStr(v, 36, "data");
  v.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let k = 0; k < samples.length; k++) {
    const c = Math.max(-1, Math.min(1, samples[k] ?? 0));
    v.setInt16(off, c < 0 ? c * 0x8000 : c * 0x7fff, true);
    off += 2;
  }
  return new Blob([buffer], { type: "audio/wav" });
}

/** Decode any browser recording and resample to 16 kHz mono WAV. */
export async function transcodeToWav16k(blob: Blob): Promise<Blob> {
  const bytes = await blob.arrayBuffer();
  const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) throw new Error(tr("No audio decoder in this browser"));
  const ctx = new Ctx();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(bytes.slice(0));
  } finally {
    void ctx.close().catch(() => undefined);
  }
  const rate = 16_000;
  const frames = Math.max(1, Math.ceil(decoded.duration * rate));
  const off = new OfflineAudioContext(1, frames, rate);
  const src = off.createBufferSource();
  src.buffer = decoded;
  src.connect(off.destination);
  src.start(0);
  const out = await off.startRendering();
  return pcmToWav(out.getChannelData(0), rate);
}

export interface PreparedAudio {
  blob: Blob;
  mime: string;
  /** Set when the transcode failed and the raw recording is being sent instead. */
  warning?: string;
}

export async function prepareAudioUpload(blob: Blob, recordedMime: string): Promise<PreparedAudio> {
  const base = baseMime(recordedMime || blob.type);
  if (base === "audio/mp4" || base === "audio/aac" || base === "audio/wav" || base === "audio/mpeg") {
    return { blob, mime: base };
  }
  try {
    const wav = await transcodeToWav16k(blob);
    return { blob: wav, mime: "audio/wav" };
  } catch {
    return {
      blob,
      mime: base || "audio/webm",
      warning: tr("Couldn't convert the recording, so the raw audio was sent. It is saved, but the transcript may fail on this one."),
    };
  }
}
